// Itinerary related models

import { IGuest, ITravelRequest, TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { ItineraryActivity } from "./activity.model";
import { ItineraryDay } from "./itinerary-day.model";
import {
  DayAxis,
  axisDayKeys,
  axisFromItinerary,
  compareDayKeys,
  datesAxis,
  dayDateFromKey,
  dayIndexFromKey,
  dayKeyFromActivity,
  indexAxis,
  isTemplateAxis,
} from "./day-axis";
import { MediaItem, normalizeMediaItem } from "@/types/media.types";

export type ItineraryStatus = 'draft' | 'pending' | 'approved' | 'completed' | 'rejected' | 'sended' | 'expired' | 'request_changes';

export type ItineraryTotals = {
  commisionable_total: number;
  not_commisionable_total: number;
  p2b_commission_percentage: number;
  td_fee_percentage: number;
  custom_fee: number;             // Sovrapprezzo admin
  // Calculated fields (not stored in DB)
  p2b_markup_gross: number;       // Fee advisor (lordo)
  vat_amount: number;             // Deprecato (compatibilità)
  p2b_markup_net: number;         // Deprecato (compatibilità)
  td_fee_amount: number;          // Guadagno Travel Designer (Fee - Place2Be fee)
  p2b_fee_amount: number;         // Place2Be fee (aliquota dell'agenzia sul totale con fee)
  total: number;                  // Totale cliente
}

export interface IItineraryData {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  /** Durata in giorni: valorizzata solo sui template, che non hanno date. */
  duration_days?: number | null;
  guests_data: IGuest[];
  budget: number;
  description: string;
  travel_request_id?: string;
  /** Cover come MediaItem, stessa forma degli elementi di `media` delle attività. */
  image?: MediaItem | null;
  custom_fee?: number;
  itinerary_days?: ItineraryDay[];
  status?: ItineraryStatus;
  note?: string;

  refundable?: 'full' | 'partial' | 'non_refundable' | null;
  commisionable_total?: number,
  not_commisionable_total?: number,
  p2b_commission_percentage: number,
  p2b_commission_type?: 'percentage' | 'fixed',
  p2b_commission_fixed_amount?: number,
  p2b_markup_gross?: number,
  vat_amount?: number,
  p2b_markup_net?: number,
  p2b_commission_amount?: number,
  td_fee_percentage: number,
  td_fee_amount?: number,
  p2b_fee_amount?: number,
  total?: number,
  cancellation_policy_notes?: string | null,
}

export class ItineraryData implements IItineraryData {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  duration_days: number | null;
  guests_data: IGuest[];
  budget: number;
  description: string;
  image: MediaItem | null;
  status?: ItineraryStatus;
  travel_request_id?: string;
  travel_request?: TravelRequest;
  itinerary_days: ItineraryDay[] = [];
  note: string;

  refundable: 'full' | 'partial' | 'non_refundable' | null;
  custom_fee: number;
  commisionable_total?: number;
  not_commisionable_total?: number;
  p2b_commission_percentage: number;
  p2b_commission_type: 'percentage' | 'fixed';
  p2b_commission_fixed_amount: number;
  p2b_markup_gross?: number;
  vat_amount?: number;
  p2b_markup_net?: number;
  p2b_commission_amount?: number;
  td_fee_percentage: number;
  td_fee_amount?: number;
  p2b_fee_amount?: number;
  total?: number;
  cancellation_policy_notes?: string | null;

  get total_guests(): number {
    return this.guests_data.length || 1;
  }

  /** Asse dei giorni: date reali o giorni relativi (template). */
  get axis(): DayAxis | null {
    return axisFromItinerary(this);
  }

  /** Un template non è collegato a nessuna richiesta e si misura in giorni. */
  get isTemplate(): boolean {
    return isTemplateAxis(this.axis);
  }

  /** Chiave del primo giorno: data ISO oppure `D1` sui template. */
  get firstDay(): string {
    return this.itinerary_days[0]?.key || this.start_date || '';
  }

  get hasTravelData(): boolean {
    if (this.isTemplate) {
      return !!(this.title && this.duration_days && this.total_guests);
    }
    return !!(
      this.title &&
      this.start_date &&
      this.end_date &&
      this.total_guests
    );
  };

  constructor(data?: IItineraryData) {
    this.id = data?.id || crypto.randomUUID();
    this.title = data?.title || '';
    // Tollera le cover legacy salvate come URL nudo (dati non ancora migrati o
    // payload provenienti da client vecchi).
    this.image = normalizeMediaItem(data?.image);
    this.start_date = data?.start_date || '';
    this.end_date = data?.end_date || '';
    this.duration_days = data?.duration_days ?? null;
    this.guests_data = data?.guests_data || [];
    this.budget = data?.budget || 0;
    this.description = data?.description || '';
    this.itinerary_days = data?.itinerary_days?.map(day => new ItineraryDay(day)) || [];
    this.travel_request_id = data?.travel_request_id || '';
    this.status = data?.status || 'draft';
    this.note = data?.note;


    this.refundable = data?.refundable ?? null;
    this.custom_fee = data?.custom_fee || 0;
    this.commisionable_total = data?.commisionable_total || 0;
    this.not_commisionable_total = data?.not_commisionable_total || 0;
    this.p2b_commission_percentage = data?.p2b_commission_percentage || 20;
    this.p2b_commission_type = data?.p2b_commission_type || 'percentage';
    this.p2b_commission_fixed_amount = data?.p2b_commission_fixed_amount || 0;
    this.p2b_commission_amount = data?.p2b_commission_amount || 0;
    this.td_fee_percentage = data?.td_fee_percentage || 97;
    this.td_fee_amount = data?.td_fee_amount || 0;
    this.total = data?.total || 0;
    this.cancellation_policy_notes = data?.cancellation_policy_notes ?? null;
  }

  fetchTravelRequest(data: ITravelRequest) {
    this.travel_request = new TravelRequest(data);
    this.travel_request_id = data.id || '';
    this.title = data.destinations?.join(', ') || '';
    this.start_date = data.travel_start_date || '';
    this.end_date = data.travel_end_date || '';
    this.guests_data = data.guests_data as IGuest[];
    this.budget = data.budget_max || 0;
    // Collegato a una richiesta: da qui in poi vive di date reali, non di durata.
    this.duration_days = null;
    this.updateItineraryDays();
  }

  update(data: Partial<IItineraryData>): ItineraryData {
    const newData = new ItineraryData({
      ...this as IItineraryData,
      ...data,
    });
    // Non aggiorniamo più automaticamente i totali per preservare i valori manuali
    newData.travel_request = this.travel_request;
    return newData;
  }

  setItineraryDays(activities: ItineraryActivity[]) {
    // L'asse dell'itinerario ha la precedenza: le attività ne fissano solo l'estensione
    // quando l'itinerario non ha ancora né date né durata.
    this.itinerary_days = generateDays(this.axis ?? axisFromActivities(activities), activities);
    return this.update({ itinerary_days: this.itinerary_days });
  }

  updateItineraryDays() {
    this.itinerary_days = mergeDays(generateDays(this.axis), this.itinerary_days);
    return this.update({ itinerary_days: this.itinerary_days });
  }

  setItineraryNote(note: string) {
    return this.update({ note });
  }

  getPayload() {
    const totals = this.extractTotals();
    return {
      id: this.id,
      title: this.title,
      // Un template salva la durata e lascia vuote le date (il DB le vuole NULL).
      start_date: this.start_date || null,
      end_date: this.end_date || null,
      duration_days: this.duration_days ?? null,
      guests_data: this.guests_data,
      budget: this.budget,
      description: this.description,
      image: this.image,
      travel_request_id: this.travel_request_id,
      status: this.status,
      note: this.note,
      refundable: this.refundable ?? null,
      // Solo i campi che esistono nella tabella del database
      commisionable_total: totals.commisionable_total,
      not_commisionable_total: totals.not_commisionable_total,
      p2b_commission_percentage: totals.p2b_commission_percentage,
      p2b_commission_amount: totals.p2b_markup_gross, // markup lordo è il commission_amount
      p2b_commission_type: this.p2b_commission_type,
      p2b_commission_fixed_amount: this.p2b_commission_fixed_amount,
      td_fee_percentage: totals.td_fee_percentage,
      custom_fee: totals.custom_fee,
      total: totals.total,
      cancellation_policy_notes: this.cancellation_policy_notes ?? null,
    }
  }

  extractAllActivities() {
    // Centralizza filtri e validazione per il salvataggio
    return this.itinerary_days.flatMap(day => day.activities)
      // Filtro attività 'out'
      .filter(activity => activity.inOrOutActivity !== 'out')
      // Valida ogni attività tramite metodo specifico
      .filter(activity => {
        if (typeof activity.validateActivity === 'function') {
          return activity.validateActivity();
        }
        // fallback: base check su id
        return !!activity.id;
      })
      // Rimuovi i campi non necessari per il salvataggio
      .map(activity => {
        // Elimina campi computati/non persistenti
        const { is_multi_day, inOrOutActivity, ...rest } = activity;
        return rest;
      });
  };


  getTotalsPayload(): Record<string, number | string> {
    const t = this.extractTotals();
    return {
      commisionable_total: t.commisionable_total,
      not_commisionable_total: t.not_commisionable_total,
      p2b_commission_percentage: t.p2b_commission_percentage,
      p2b_commission_amount: t.p2b_markup_gross,
      p2b_commission_type: this.p2b_commission_type,
      p2b_commission_fixed_amount: this.p2b_commission_fixed_amount,
      td_fee_percentage: t.td_fee_percentage,
      custom_fee: t.custom_fee,
      total: t.total,
    };
  }

  /**
   * @param platformFeeRate aliquota della fee Place2Be (frazione) per questo viaggio, da
   *   `get_itinerary_platform_fee_rate`: ogni agenzia o indipendente ha la sua. Serve solo
   *   per p2b_fee_amount e td_fee_amount, che si mostrano e non si salvano; senza, quei
   *   due campi valgono 0 e markup, e chi li mostra deve dire che l'aliquota manca.
   */
  extractTotals(platformFeeRate: number | null = null): ItineraryTotals {
    const allActivities = this.extractAllActivities();
    
    // LOGICA COMMISSIONI (definita in meeting):
    // 1. Totale base = costo servizi (voli + hotel + attività)
    // 2. Markup = fee advisor (% del totale servizi, modificabile)
    // 3. Totale finale = Totale base + Markup (quello che paga il cliente)
    // 4. Place2Be fee = aliquota dell'agenzia sul Totale finale (fee sul "transato")
    // 5. Guadagno advisor = Markup - Place2Be fee
    
    const p2b_commission_percentage = this.p2b_commission_percentage || 20; // Fee advisor (default 20%)
    const td_fee_percentage = 97; // Deprecato, manteniamo per compatibilità DB
    const custom_fee = this.custom_fee || 0; // Sovrapprezzo admin

    const result: ItineraryTotals = {
      commisionable_total: 0,
      not_commisionable_total: 0,
      p2b_commission_percentage,
      td_fee_percentage,
      custom_fee,
      p2b_markup_gross: 0,
      vat_amount: 0,
      p2b_markup_net: 0,
      td_fee_amount: 0,
      p2b_fee_amount: 0,
      total: 0,
    }

    // 1) Somma i costi delle attività (Totale base)
    allActivities.forEach(activity => {
      if (!activity.include_price || !('cost' in activity)) return;
      if (activity.type === 'flight') {
        result.not_commisionable_total += activity.cost;
      } else {
        result.commisionable_total += activity.cost;
      }
    });

    // 2) Calcola Markup advisor: cifra fissa o % del totale servizi.
    //    La base è tutto ciò che il cliente paga (voli inclusi), non solo il commissionabile.
    const services_total = result.commisionable_total + result.not_commisionable_total;
    if (this.p2b_commission_type === 'fixed') {
      result.p2b_markup_gross = this.p2b_commission_fixed_amount || 0;
    } else {
      result.p2b_markup_gross = services_total * (p2b_commission_percentage / 100);
    }

    // 3) Totale finale = Totale base + Markup (quello che paga il cliente, senza custom fee)
    const totalWithFee = services_total + result.p2b_markup_gross;
    
    // 4) Place2Be prende la sua aliquota sul Totale finale (fee sul "transato")
    result.p2b_fee_amount = platformFeeRate !== null ? totalWithFee * platformFeeRate : 0;
    
    // 5) Advisor riceve: Markup - Place2Be fee
    result.td_fee_amount = result.p2b_markup_gross - result.p2b_fee_amount;
    
    // 6) Campi deprecati (per compatibilità con vecchio sistema)
    result.vat_amount = 0;
    result.p2b_markup_net = result.p2b_markup_gross;
    
    // 7) Totale finale cliente = Totale base + Markup + Custom fee
    result.total = totalWithFee + custom_fee;

    return result;
  };

  // Note: updateTotals removed - totals are calculated on-the-fly via extractTotals()
  // Only percentages (p2b_commission_percentage, td_fee_percentage) and custom_fee are persisted
}

function mergeDays(originalDays: ItineraryDay[], newDays: ItineraryDay[]): ItineraryDay[] {
  if (!originalDays.length) return newDays;
  if (!newDays.length) return originalDays;

  const byKey = new Map(newDays.map(day => [day.key, day]));
  return originalDays.map(day => byKey.get(day.key) || day);
}

/**
 * Ricava un asse dalle attività, per gli itinerari che non hanno ancora né date né durata.
 */
function axisFromActivities(activities: ItineraryActivity[]): DayAxis | null {
  if (!activities.length) return null;

  // Un template: le attività portano solo indici.
  if (activities.every(a => !a.date)) {
    return indexAxis(Math.max(...activities.map(a => a.end_day_index || a.day_index || 1)));
  }

  const { start_date, end_date } = getStartAndEndDateFromActivities(activities);
  return start_date && end_date ? datesAxis(start_date, end_date) : null;
}

/**
 * Costruisce i giorni dell'asse, distribuendoci le attività in base al loro ancoraggio.
 *
 * Le attività che cadono fuori asse (check-out il giorno dopo l'ultimo, o un template
 * più lungo della richiesta a cui è stato collegato) non vengono scartate: restano
 * visibili come giorni in coda, che il builder segnala.
 */
function generateDays(axis: DayAxis | null, activities: ItineraryActivity[] = []): ItineraryDay[] {
  if (!axis) return [];

  // Una sola passata sulle attività: il raggruppamento per giorno vale sia per i
  // giorni dell'asse sia per quelli fuori asse.
  const activitiesByKey = new Map<string, ItineraryActivity[]>();
  for (const activity of activities) {
    const key = dayKeyFromActivity(axis, activity);
    if (!key) continue;
    const bucket = activitiesByKey.get(key);
    if (bucket) bucket.push(activity);
    else activitiesByKey.set(key, [activity]);
  }

  const axisKeys = axisDayKeys(axis);
  const onAxis = new Set(axisKeys);
  const extraKeys = [...activitiesByKey.keys()].filter(key => !onAxis.has(key));

  return [...axisKeys, ...extraKeys]
    .sort(compareDayKeys)
    .map(key => new ItineraryDay({
      key,
      date: dayDateFromKey(axis, key),
      day_index: dayIndexFromKey(axis, key),
      activities: activitiesByKey.get(key) || [],
    }));
}

function getStartAndEndDateFromActivities(activities: ItineraryActivity[]): { start_date: string, end_date: string } {
  if (!activities.length) {
    return { start_date: '', end_date: '' };
  }

  let start_date = activities[0].date;
  let end_date = activities[0].date;

  for (let i = 1; i < activities.length; i++) {
    const currentDate = activities[i].date;
    if (currentDate < start_date) {
      start_date = currentDate;
    }
    if (currentDate > end_date) {
      end_date = currentDate;
    }
  }

  return { start_date, end_date };
}